import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Divider,
  IconButton,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Category as CategoryIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as TimeIcon,
  Home as PropertyIcon,
  Comment as CommentIcon,
  LocationOn as LocationIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import palette from 'src/theme/palette';
import useMediaQuery from '@mui/material/useMediaQuery';

const ViewInquiryModal = ({ open, onClose, inquiry, onSuccess }) => {
  const isMobile = useMediaQuery('(max-width:600px)');
  // Render property details in modal (same as contact modal)
  const renderPropertyDetails = () => {
    if (!inquiry.property) {
      return null;
    }

    const property = inquiry.property;
    const formatPrice = () => {
      if (!property.price) return 'N/A';

      if (property.is_area_unit === '1' && property.area_unit) {
        return `$${property.price.toLocaleString()} per ${property.area_unit.name.toLowerCase()}`;
      } else {
        return `$${property.price.toLocaleString()}`;
      }
    };
    const formatArea = () => {
      if (!property.area_size) return 'N/A';

      const area = property.area_size.toLocaleString();
      if (property.area_unit) {
        return `${area} ${property.area_unit.name.toLowerCase()}`;
      }
      return `${area} `;
    };

    return (
      <>
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* Property Details Section */}
        <Grid item xs={12}>
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                color: palette.text.secondary,
                fontFamily: 'poppinsMedium',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <PropertyIcon color="primary" fontSize="small" />
              Property Details
            </Typography>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              {/* Property Image */}
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  overflow: 'hidden',
                  flexShrink: 0,
                  backgroundColor: palette.grey[200]
                }}
              >
                {property.main_image ? (
                  <img
                    src={property.main_image}
                    alt={property.title}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: property.main_image ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: palette.grey[300]
                  }}
                >
                  <Typography variant="body2" color="textSecondary">
                    No Image
                  </Typography>
                </Box>
              </Box>

              {/* Property Info */}
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'poppinsSemiBold',
                      fontSize: '18px',
                      color: palette.text.primary,
                      lineHeight: 1.2
                    }}
                  >
                    {property.title || 'N/A'}
                  </Typography>

                  {/* Property Link Button */}
                  {property.property_link && (
                    <Button
                      component="a"
                      href={property.property_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      endIcon={<OpenInNewIcon />}
                      sx={{
                        textTransform: 'none',
                        fontFamily: 'poppinsMedium',
                        fontSize: '12px',
                        minWidth: 'auto'
                      }}
                    >
                      View Property
                    </Button>
                  )}
                </Box>

                {property.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationIcon sx={{ fontSize: 16, color: palette.text.secondary, mr: 0.5 }} />
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: palette.text.secondary,
                        lineHeight: 1.2
                      }}
                    >
                      {property.address}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '13px',
                      color: palette.text.disabled,
                      lineHeight: 1.2
                    }}
                  >
                    {property.city || 'N/A'}
                    {property.state && `, ${property.state}`}
                  </Typography>
                </Box>

                {/* Property Stats */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {property.price && (
                    <Chip label={formatPrice()} variant="outlined" color="primary" size="small" />
                  )}
                  {property.bedrooms && (
                    <Chip label={`${property.bedrooms} Beds`} variant="outlined" size="small" />
                  )}
                  {property.bathrooms && (
                    <Chip label={`${property.bathrooms} Baths`} variant="outlined" size="small" />
                  )}
                  {property.area_size && (
                    <Chip label={formatArea()} variant="outlined" size="small" />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  const renderPersonalInfoSection = () => (
    <>
      <Grid item xs={12}>
        <Typography
          variant="h6"
          fontFamily="poppinsSemiBold"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <PersonIcon color="primary" />
          Personal Information
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PersonIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Name
          </Typography>
        </Box>
        <Typography variant="body1" fontFamily="poppinsRegular" sx={{ ml: 4 }}>
          {inquiry?.name || 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <EmailIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Email
          </Typography>
        </Box>
        <Typography variant="body1" fontFamily="poppinsRegular" sx={{ ml: 4 }}>
          {inquiry.email || 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PhoneIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Contact Number
          </Typography>
        </Box>
        <Typography variant="body1" fontFamily="poppinsRegular" sx={{ ml: 4 }}>
          {inquiry.contact_no || 'N/A'}
        </Typography>
      </Grid>
    </>
  );

  const renderAppointmentSection = () => (
    <>
      <Grid item xs={12}>
        <Typography
          variant="h6"
          fontFamily="poppinsSemiBold"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
        >
          <CalendarTodayIcon color="primary" />
          Appointment Details
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CalendarTodayIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Date
          </Typography>
        </Box>
        <Typography variant="body1" fontFamily="poppinsRegular" sx={{ ml: 4 }}>
          {inquiry.date || 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <TimeIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Time
          </Typography>
        </Box>
        <Typography variant="body1" fontFamily="poppinsRegular" sx={{ ml: 4 }}>
          {inquiry.time ? inquiry.time.substring(0, 5) : 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CategoryIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Type
          </Typography>
        </Box>
        <Typography
          variant="body1"
          fontFamily="poppinsRegular"
          sx={{ ml: 4 }}
          textTransform="capitalize"
        >
          {/* {inquiry?.type?.name('_', ' ') || 'N/A'} */}
          {inquiry?.type?.name || 'N/A'}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CategoryIcon color="action" fontSize="small" />
          <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
            Status
          </Typography>
        </Box>
        <Typography
          variant="body1"
          fontFamily="poppinsRegular"
          sx={{ ml: 4 }}
          textTransform="capitalize"
        >
          {inquiry?.status || 'Pending'}
        </Typography>
      </Grid>
    </>
  );

  const renderClientRequirementsSection = () =>
    !inquiry.property && inquiry.comment ? (
      <>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontFamily="poppinsSemiBold"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
          >
            <CommentIcon color="primary" />
            Client Requirements
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
            <CommentIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
              Requirements & Comments
            </Typography>
          </Box>
          <Typography
            variant="body1"
            fontFamily="poppinsRegular"
            sx={{
              ml: 4,
              whiteSpace: 'pre-wrap',
              backgroundColor: palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${palette.grey[300]}`
            }}
          >
            {inquiry.comment || 'No specific requirements provided'}
          </Typography>
        </Grid>
      </>
    ) : null;

  const renderCommentSection = () =>
    inquiry.comment && inquiry.property ? (
      <>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            fontFamily="poppinsSemiBold"
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
          >
            <CommentIcon color="primary" />
            Additional Comments
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Grid>

        <Grid item xs={12}>
          <Box display="flex" alignItems="flex-start" gap={1} mb={1}>
            <CommentIcon color="action" fontSize="small" />
            <Typography variant="subtitle2" color="textSecondary" fontFamily="poppinsMedium">
              Description
            </Typography>
          </Box>
          <Typography
            variant="body1"
            fontFamily="poppinsRegular"
            sx={{
              ml: 4,
              whiteSpace: 'pre-wrap',
              backgroundColor: palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${palette.grey[300]}`
            }}
          >
            {inquiry.comment}
          </Typography>
        </Grid>
      </>
    ) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          margin: { xs: 2, sm: 3 },
          maxHeight: isMobile ? '100vh' : '90vh',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          width: isMobile ? '100vw' : '1400px',
          margin: isMobile ? 0 : undefined
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          background: palette.primary.main,
          color: palette.primary.contrastText,
          py: 2,
          px: 3,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontFamily: 'poppinsSemiBold',
            color: palette.primary.contrastText,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <PersonIcon />
          Inquiry Details
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: palette.primary.contrastText,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {inquiry ? (
          <Grid container spacing={3}>
            {renderPersonalInfoSection()}
            {renderAppointmentSection()}

            {/* Property Details - Conditionally shown if property exists */}
            {renderPropertyDetails()}

            {renderClientRequirementsSection()}
            {renderCommentSection()}
          </Grid>
        ) : (
          <Typography>No inquiry data available</Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: palette.primary.main,
            color: palette.primary.contrastText,
            minWidth: 100,
            '&:hover': {
              backgroundColor: palette.primary.dark
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewInquiryModal;
